import { localStorageChecks } from './emailSignupLocalStorage.ts';

/**
 * Just a way of telling TypeScript what values we might expect in qsParam here.
 */
interface PossibleQueryStringParams {
    showLeadGen?: string
}
const internalChecks = (leadGenEnabled: boolean) => {
    if (!leadGenEnabled) {
        return RESULT.NEVER_SHOW;
    }

    if ((qsParam as PossibleQueryStringParams).showLeadGen) {
        return RESULT.SHOW_IMMEDIATELY;
    }

    return null;
};

/**
 * Just a way of telling TypeScript about the properties this code expects `userObj` to have.
 */
interface PartialUserObj {
    userId?: number,
}

const hasUser = () => {
    // userObj *roughly* corresponds to an actively-logged-in user, and userType *roughly* corresponds to ever having
    // been signed in at all.
    return !!((userObj as PartialUserObj).userId) || !!readCookie('userType');
};

const pageLevelChecks = (leadGenEnabled: boolean) => {
    const internalResult = internalChecks(leadGenEnabled);
    if (internalResult) {
        return internalResult;
    }

    if (hasUser()) {
        return RESULT.NEVER_SHOW;
    }

    return RESULT.CHECK_LOCALSTORAGE;
};

/**
 * Call `showFeature` if the user scrolls down to the point where `#mainFooter` is visible.
 */
const addEventHandlers = (showFeature: () => void, targetElementId: string = 'mainFooter') => {
    const targetElement = document.getElementById(targetElementId)!;
    let observer: IntersectionObserver;
    const wrappedShowFeature = (entries: IntersectionObserverEntry[]) => {
        if (!entries[0].isIntersecting) {
            // The observer-callback runs once as soon as you call `observe`, even if the element isn't visible.
            // You have to check `isIntersecting` to see if the element is actually visible right now.
            return;
        }

        // Don't show this more than once
        observer.unobserve(targetElement);
        showFeature();
    };
    observer = new IntersectionObserver(wrappedShowFeature, {
        root: null,
        threshold: 0,
    });

    observer.observe(targetElement);
};

/**
 * Deciding whether or not to show the "lead gen" email signup form happens in a few steps.
 *
 * First: "internal" stuff. Things like "did they add the special `showLeadGen` parameter we added for the sake of
 * manual testing in test environments?", or "is this feature flagged off?". These shouldn't change much. But, as those
 * two examples suggest, this isn't boolean. Some of these conditions short-circuit out to "force show immediately,
 * check no other conditions", others short-circuit to "do not show under any circumstances", others fall through to
 * "maybe set up on-scroll handlers etc. to decide whether or not to show this".
 *
 * Second, we should check for conditions under which we wouldn't want to show this at all. Things like "we know from
 * `window.userObj` that this user already has some kind of account". These conditions are little bit more
 * business-rules-y, but they should reduce to a boolean - if false, don't show this, if true, keep going.
 *
 * Third, we check localstorage to see if/when the user has already closed or filled out this form. Because of our
 * subdomain-splitting architecture, this (like all localstorage checks) has to happen asynchronously, because it may or
 * may not involve an AJAX request against `secure`.
 *
 * Fourth, we have some event handlers that get repeatedly re-run to decide whether we should show this _now_. Things
 * like "has the user scrolled down far enough" go here. These are also business-rules-y.
 */
export const checkShowConditions = (leadGenEnabled: boolean, minimizedLeadGenEnabled: boolean, showFeature: () => void, targetElementId?: string) => {
    const pageLevelResult = pageLevelChecks(leadGenEnabled);

    if (pageLevelResult === RESULT.SHOW_IMMEDIATELY) {
        showFeature();
        return;
    }

    if (pageLevelResult === RESULT.CHECK_LOCALSTORAGE) {
        localStorageChecks(() => {
            addEventHandlers(showFeature, targetElementId);
        }, minimizedLeadGenEnabled);
    }
};

const RESULT = {
    SHOW_IMMEDIATELY: 'SHOW_IMMEDIATELY',
    NEVER_SHOW: 'NEVER_SHOW',
    CHECK_LOCALSTORAGE: 'CHECK_LOCALSTORAGE',
};
